/**
 * Which bracket strings in ad copy the token registry answers for (MPN-1930).
 *
 * A copywriter types `[SCHOOL NAM]` and nothing tells them: the string is saved and
 * published exactly as written, and the typo surfaces when somebody reads the live ad. This
 * is the check, run live in the editor against the vocabulary the picker already loaded.
 *
 * Nothing here blocks anything. A finding is a note beside the field, the copy saves either
 * way, and an author who meant `[FOO]` literally ignores it -- the same contract as the
 * network rules (MPN-1616).
 *
 * Pure functions of the text and the vocabulary, so which strings land in which kind is
 * testable without rendering anything, and so they work on any string: an ad copy field
 * today, a Create Studio text layer or keyframe value later (MPN-1933).
 */
import { adTokenLabel, isCopywriterInstruction, normalizeAdTokenText, type AdToken } from "./adTokens";

/**
 * One pair of square brackets with 1-128 characters between them, none of them a bracket or
 * a line break. 128 is the longest spelling the registry can store.
 *
 * The bounds are what stop a stray `[` swallowing everything up to the next `]` further down
 * the copy. `u` makes the 128 count characters, as the registry does, rather than UTF-16
 * units -- without it an emoji counts twice.
 */
const AD_TOKEN_IN_TEXT = /\[[^[\]\n]{1,128}\]/gu;

/**
 * What a bracket string is to the registry.
 *
 *   known       -- a canonical spelling. Fills itself in.
 *   alias       -- another spelling of a token. It resolves, but the canonical is what the
 *                  registry is curated around, so the editor offers it.
 *   instruction -- a canonical or alias of a copywriter instruction (`is_automatable`
 *                  false). Registered, so not a typo, but nothing will ever fill it in.
 *   unknown     -- nothing in the vocabulary. A typo, a placeholder never registered, or a
 *                  retired token: the endpoint lists active tokens only.
 */
export type AdTokenKind = "known" | "alias" | "instruction" | "unknown";

export type AdTokenRange = {
    /** Index of the `[` in the text. */
    start: number;
    /** Index just past the `]`. */
    end: number;
    /** The bracket string as written. */
    raw: string;
    /** `raw` under the spelling rule. Two ranges with the same one are the same token. */
    normalized: string;
    kind: AdTokenKind;
    /** The token's canonical spelling, for everything but `unknown`. */
    canonical?: string;
};

type Entry = { token: AdToken; canonical: boolean };

/** One lookup table per vocabulary, built on first use rather than once per keystroke. */
const indexes = new WeakMap<AdToken[], Map<string, Entry>>();

/**
 * Every spelling the vocabulary answers to, normalized. Aliases may be stored with or
 * without their brackets, so each spelling is re-bracketed before it is normalized. A
 * canonical is indexed before any alias, so if one ever collided with another token's alias
 * the canonical would win -- the server's unique index means it cannot.
 */
function indexOf(tokens: AdToken[]): Map<string, Entry> {
    let index = indexes.get(tokens);
    if (index) return index;
    index = new Map();
    const key = (spelling: string) => normalizeAdTokenText(`[${adTokenLabel(spelling)}]`);
    for (const token of tokens) index.set(key(token.token), { token, canonical: true });
    for (const token of tokens) {
        for (const alias of token.aliases) {
            if (!index.has(key(alias))) index.set(key(alias), { token, canonical: false });
        }
    }
    indexes.set(tokens, index);
    return index;
}

function kindOf(entry: Entry | undefined): AdTokenKind {
    if (!entry) return "unknown";
    if (isCopywriterInstruction(entry.token)) return "instruction";
    return entry.canonical ? "known" : "alias";
}

/** Every bracket string in the text, in order, each classified against the vocabulary. */
export function scanAdTokens(text: string, tokens: AdToken[]): AdTokenRange[] {
    const index = indexOf(tokens);
    return Array.from(text.matchAll(AD_TOKEN_IN_TEXT), (match) => {
        const raw = match[0];
        const normalized = normalizeAdTokenText(raw);
        const entry = index.get(normalized);
        return {
            start: match.index,
            end: match.index + raw.length,
            raw,
            normalized,
            kind: kindOf(entry),
            ...(entry && { canonical: entry.token.token }),
        };
    });
}

/** What the editor says about one token in one field. `known` says nothing. */
export type AdTokenNote = {
    kind: Exclude<AdTokenKind, "known">;
    /** The first spelling of this token in the field, as written. */
    raw: string;
    normalized: string;
    canonical?: string;
    message: string;
};

/**
 * The notes for one field: one per token, not one per use, in the order they first appear.
 * A token used five times in a paragraph is one thing to fix, not five. Reported against the
 * first spelling seen, so `[term name]` and `[TERM NAME]` in one field are one note.
 */
export function adTokenNotes(ranges: AdTokenRange[]): AdTokenNote[] {
    const notes: AdTokenNote[] = [];
    const seen = new Set<string>();
    for (const range of ranges) {
        if (range.kind === "known" || seen.has(range.normalized)) continue;
        seen.add(range.normalized);
        const { kind, raw, normalized, canonical } = range;
        const message =
            kind === "unknown" ? `${raw} isn't a known ad token`
            : kind === "alias" ? `${raw} will be read as ${canonical}`
            : `${raw} -- you fill this in`;
        notes.push({ kind, raw, normalized, canonical, message });
    }
    return notes;
}

/**
 * Rewrite the first alias spelled `normalized` to its canonical, or null if the text no
 * longer has one -- the note may be a debounce behind what is in the field now, so it is
 * looked for again rather than trusted by position. The caret goes after the new token.
 */
export function replaceAliasWithCanonical(
    text: string,
    tokens: AdToken[],
    normalized: string,
): { value: string; caret: number } | null {
    const range = scanAdTokens(text, tokens).find((r) => r.kind === "alias" && r.normalized === normalized);
    if (!range?.canonical) return null;
    return {
        value: text.slice(0, range.start) + range.canonical + text.slice(range.end),
        caret: range.start + range.canonical.length,
    };
}

/** Whether the text has anything in it that reads as an ad token at all. */
export function containsAdTokenSyntax(text: string): boolean {
    return scanAdTokens(text, []).length > 0;
}

/** A run of the field's text, and the kind of token it is, if it is one. */
export type AdTokenSegment = { text: string; kind: AdTokenKind | null };

/**
 * The text cut into runs at the token ranges, so a backdrop can color the tokens and lay the
 * rest out unstyled. The runs concatenate back to exactly `text` -- the backdrop is only in
 * line with the field while that holds.
 */
export function adTokenSegments(text: string, ranges: AdTokenRange[]): AdTokenSegment[] {
    const segments: AdTokenSegment[] = [];
    let at = 0;
    for (const range of ranges) {
        if (range.start > at) segments.push({ text: text.slice(at, range.start), kind: null });
        segments.push({ text: range.raw, kind: range.kind });
        at = range.end;
    }
    if (at < text.length) segments.push({ text: text.slice(at), kind: null });
    return segments;
}
